
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { format } from "date-fns";
import { Plane } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { useTranslation } from "react-i18next";
import { enUS, fr, es, it, de, ru, sv, pt, zhTW, arSA } from "date-fns/locale";
import { FlutterUtils } from "@/hooks/useFlutterBridge";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

interface UpcomingFlightsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const FlightContent = ({
  open,
  onOpenChange,
}: UpcomingFlightsDialogProps) => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { i18n, t } = useTranslation();
  const [groupedNotifications, setGroupedNotifications] = useState<{ [key: string]: any[] }>({});
  const [sortedGroups, setSortedGroups] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Get appropriate locale based on current language
  const getLocale = () => {
    switch (i18n.language) {
      case 'fr':
        return fr;
      case 'es':
        return es;
      case 'it':
        return it;
      case 'de':
        return de;
      case 'ru':
        return ru;
      case 'sv':
        return sv;
      case 'pt':
        return pt;
      case 'zh':
        return zhTW;
      case 'ar':
        return arSA;
      default:
        return enUS;
    }
  };

  const formatSentTime = (sentTime: string) => {
    const date = new Date(sentTime);
    return format(date, "h:mm a", { locale: getLocale() });
  };

  const reRunNotification = (e:any, notification: any) => {
      e.stopPropagation();
      e.preventDefault();
      if (notification) {
        let isRequestingToCloseDialog = FlutterUtils.executeNotification(notification, navigate);
        if (isRequestingToCloseDialog) {
          onOpenChange(false);
        }
        return;
      }
  }

  const updateGroupNotificationsByDay = (notifications: any[]) => {
    const groups: { [key: string]: any[] } = {};

    for (let notification of notifications) {
      const date = new Date(notification.sentTime);
      const now = new Date();
      const isToday = date.toDateString() === now.toDateString();
      const isYesterday =
        date.toDateString() ===
        new Date(now.setDate(now.getDate() - 1)).toDateString();

      let groupKey;
      if (isToday) {
        groupKey = t('tripCard.today');
      } else if (isYesterday) {
        groupKey = t('tripCard.dayAgo');
      } else {
        groupKey = format(date, "MMM d, yyyy", { locale: getLocale() });
      }

      if (!groups[groupKey]) {
        groups[groupKey] = [];
      }
      groups[groupKey].push(notification);
    };
    

    // Sort notifications within each group by sentTime
    for (const groupKey of Object.keys(groups)) {
      groups[groupKey].sort((a, b) => new Date(b.sentTime).getTime() - new Date(a.sentTime).getTime());
    }
    
    // Sort groups by date
    const sortedGroups = Object.keys(groups).sort((a, b) => {
      const dateA = new Date(groups[a][0].sentTime);
      const dateB = new Date(groups[b][0].sentTime);
      return dateB.getTime() - dateA.getTime();
    });

    setGroupedNotifications(groups);
    setSortedGroups(sortedGroups);
    // console.debug("Grouped notifications:", JSON.stringify(groups));
    // console.debug("Sorted groups:", JSON.stringify(sortedGroups));
  };

  useQuery({
    queryKey: ['pastNotifications'],
    queryFn: () => {
      if (FlutterUtils.isFlutterWebapp()) {
        console.log("Requesting past notifications...");
        FlutterUtils.requestPastNotifications((pastNotifs) => {
          console.log("Received past notifications:", pastNotifs);
          updateGroupNotificationsByDay(pastNotifs);
          setIsLoading(false);
        });
      }
      else
      {
        console.log("Requesting past notifications from API...");
        setIsLoading(false);
      }
    },
  });


  return (
    <div className="space-y-6">
      {isLoading && (
        <div className="text-center py-8">
          <div className="h-12 w-12 mx-auto mb-3 border-4 border-travel-200 border-t-travel-600 rounded-full animate-spin" />
          <p className="text-travel-600 font-medium">
            {t('tripCard.loadingNotifications', 'Loading notifications...')}
          </p>
        </div>
      )}
      {!isLoading && sortedGroups.length > 0 && (
        <div>
          {sortedGroups.map((day) => (
            <div key={day} className="space-y-4">
              <div className="sticky top-0 bg-travel-50/90 backdrop-blur-sm px-4 py-2.5 rounded-md text-center text-sm font-semibold text-travel-900 shadow-sm border border-travel-100">
                {day}
              </div>
              {groupedNotifications[day] && groupedNotifications[day].map((notification, index) => (
                <div
                  key={index}
                  className="bg-white rounded-lg p-4 border border-travel-300 shadow-md transition-all duration-200"
                >
                  <div className="flex items-start space-x-3 clickable" onClick={(e) => reRunNotification(e, notification)}>
                    <div className="flex-1">
                      <p className="text-sm text-gray-900 leading-relaxed">
                        {notification.notificationBody}
                      </p>
                      <p className="text-xs text-muted-foreground mt-2 text-right">
                        {formatSentTime(notification.sentTime)}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
      {!isLoading && sortedGroups.length === 0 && (
        <div className="text-center py-8">
          <p className="text-travel-600 font-medium">
            {t('tripCard.noNotifications', 'No notifications yet')}
          </p>
        </div>
      )}
    </div>
  );
};

export function UpcomingFlightsDialog({
  open,
  onOpenChange,
}: UpcomingFlightsDialogProps) {
  const { t } = useTranslation();
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto overscroll-contain scroll-smooth p-6">
        <DialogHeader>
          <DialogTitle>{t('tripCard.notifications', 'Notifications')}</DialogTitle>
          <DialogDescription>
            {t('tripCard.notificationsDescription', 'Important notifications about your trips')}
          </DialogDescription>
        </DialogHeader>
        <FlightContent open={open} onOpenChange={onOpenChange} />
      </DialogContent>
    </Dialog>
  );
}

UpcomingFlightsDialog.Content = FlightContent;
